/**
 * DDD Package - Core Building Blocks Export
 * 
 * TEMPORARY: This export is to maintain compatibility during refactoring.
 * 
 * ARCHITECTURAL NOTE: 
 * Each bounded context should ideally have its own domain building blocks
 * rather than sharing them across contexts. This violates DDD principles
 * of bounded context autonomy.
 * 
 * TODO: Refactor to move shared building blocks to @swoft/core-application
 * or have each context implement its own domain layer.
 */

// CRITICAL: Export DDD_SYMBOLS first to ensure initialization before decorators
export { DDD_SYMBOLS } from './infrastructure/di/DDD_SYMBOLS';

// Core DDD Building Blocks from @swoft/core-architecture
export { Entity } from '@swoft/core';
export { EntityId } from '@swoft/core';
export type { DomainEvent } from '@swoft/core';

// Domain Entities (these should NOT be shared across bounded contexts)
export type { Aggregate } from './bounded-contexts/tactical-design/domain/entities/Aggregate';
export { TacticalDesign } from './bounded-contexts/tactical-design/domain/entities/TacticalDesign';

// Strategic Design Entities
export { Domain } from './bounded-contexts/strategic-design/domain/entities/Domain';
export { BoundedContext } from './bounded-contexts/strategic-design/domain/entities/BoundedContext';
export { Project } from './bounded-contexts/strategic-design/domain/entities/Project';

// Package Mapping Entities and Types
export { PackageMapping, PackageType, MappingStatus } from './bounded-contexts/meta-model-design/domain/entities/PackageMapping';
export type { PackageAnalysis, PackageClassification } from './bounded-contexts/meta-model-design/domain/entities/PackageMapping';

// Package Mapping Repository - Clean Types/Symbols Pattern
export type * from './bounded-contexts/meta-model-design/domain/ports/types';
export * from './bounded-contexts/meta-model-design/domain/ports/symbols';

// ============================================
// INTEGRATION CONTRACTS (Published Language)
// ============================================
// Strategic DDD - Published Language for downstream contexts
export * from './integration';

// Application Services (these are the proper integration points)
export { AggregateApplicationService } from './application/services/AggregateApplicationService';
export { DomainApplicationService } from './application/services/DomainApplicationService';
export { BoundedContextApplicationService } from './application/services/BoundedContextApplicationService';
export { ProjectApplicationService } from './application/services/ProjectApplicationService';
export { PackageMappingApplicationService } from './bounded-contexts/meta-model-design/application/PackageMappingApplicationService';

// Controllers (Interface Layer)
export { DomainController } from './bounded-contexts/strategic-design/interface/controllers/DomainController';
export { BoundedContextController } from './bounded-contexts/strategic-design/interface/controllers/BoundedContextController';
export { ProjectController } from './bounded-contexts/strategic-design/interface/controllers/ProjectController';
export { AggregateController } from './bounded-contexts/tactical-design/interface/controllers/AggregateController';
export { PackageMappingController } from './bounded-contexts/meta-model-design/interface/controllers/PackageMappingController';

// Service Factory (NEW: Replaces Inversify-based DDDModule)
export { ServiceFactory } from './infrastructure/factory/ServiceFactory';

// Modern ContainerModule Pattern (Recommended - matches party-manager)
export { createDDDModule } from './bounded-contexts/meta-model-design/infrastructure/di/DDDContainerModule';

// Meta-Model Design Bounded Context
export { createMetaModelDesignModule, META_MODEL_DESIGN_SYMBOLS } from './bounded-contexts/meta-model-design/infrastructure/di/MetaModelDesignContainerModule';
export { BoundedContextAnalysisService } from './bounded-contexts/meta-model-design/application/services/BoundedContextAnalysisService';
export type { 
  AnalyzeBoundedContextRequest, 
  AnalyzeMonorepoRequest,
  BoundedContextAnalysisResult,
  MonorepoAnalysisResult,
  ComplianceReportResult 
} from './bounded-contexts/meta-model-design/application/services/BoundedContextAnalysisService';

// MongoDB Collections Configuration
export * from './config/collections';

// Domain Seeder
export * from './infrastructure/DDDSeeder';

// ============================================
// DOMAIN RULES (for DDD Health Analysis)
// ============================================
export { createDDDDomainRules } from './validation';

// ============================================
// AI TOOLS BOUNDED CONTEXT - Pure Vercel AI Tools
// ============================================
// Pure DDD Analysis Tools (No Dependencies)
export { 
  dddAnalysisTools,
  exploreStructureTool,
  findViolationsTool,
  generateHealthReportTool
} from './bounded-contexts/ai-tools/domain/tools/ddd-analysis-tools';

// ============================================
// DOMAIN EXPERTS BOUNDED CONTEXT
// ============================================
// TODO: Fix Domain Experts bounded context integration
// The domain experts configuration needs to be refactored to work with
// the new module pattern. Currently disabled to avoid runtime errors.
// export {
//   // Routes
//   createDomainExpertRoutes,
//   // Controller
//   DomainExpertController
// } from './bounded-contexts/domain-experts';